import { createPortal } from "react-dom"
import { useQuery } from "@tanstack/react-query"
import { Download, Paperclip } from "lucide-react"
import { ModalShell } from "@/components/portal/ModalShell"
import { EmailBody } from "@/components/portal/EmailBody"
import { fmtDateLong } from "@/pages/emails/format"
import { postfachParams } from "./postfachParams"

/**
 * Lese-Fenster für eine Mail LIVE aus dem IMAP-Postfach (DSGVO, 02.10.2026).
 *
 * Genutzt von der Postfach-Suche (Treffer per `uid`) und überall dort, wo eine
 * Portal-Mail nach 90 Tagen geleert ist (per `messageId` + Ordner aus der
 * `email_log`-Zeile). 🔴 Nur lesen — nichts wird im Portal gespeichert.
 */

type PostfachMail = {
  verfuegbar: boolean
  gefunden?: boolean
  fehler?: string | null
  folder?: string | null
  folder_label?: string
  subject?: string
  sender?: string
  to?: string
  cc?: string
  date?: string | null
  body_text?: string
  body_html?: string
  attachments?: { index: number; filename: string; content_type: string; size: number; inline: boolean }[]
}

function groesse(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

/**
 * Eine Mail live aus dem Postfach — nur lesen. Mit `uid` (Suchtreffer) oder
 * `messageId` (z. B. eine Portal-Mail, deren Inhalt schon geleert ist).
 */
export function PostfachMailModal({
  tenant, postfach, folder, uid, messageId, onClose,
}: {
  tenant: string
  postfach?: string | null
  folder: string
  uid?: string
  messageId?: string
  onClose: () => void
}) {
  const kennung = { tenant, postfach: postfach || "", folder, uid: uid || "", message_id: messageId || "" }
  const mail = useQuery<PostfachMail>({
    queryKey: ["postfach-mail", kennung],
    queryFn: async () => {
      const r = await fetch(`/api/v1/emails/postfach-mail?${postfachParams(kennung)}`, { credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return r.json()
    },
    retry: false,
    // Inhalt nicht im Speicher behalten, wenn das Fenster zu ist.
    gcTime: 0,
  })
  const m = mail.data

  return createPortal(
    <ModalShell
      open
      onClose={onClose}
      breite={900}
      titel={m?.subject || (mail.isLoading ? "Lädt…" : "(kein Betreff)")}
      untertitel={m?.gefunden && (
        <span style={{ display: "block", lineHeight: 1.6 }}>
          <strong style={{ color: "#475569" }}>Von:</strong> {m.sender}
          {m.to && <> · <strong style={{ color: "#475569" }}>An:</strong> {m.to}</>}
          {m.cc && <> · <strong style={{ color: "#475569" }}>CC:</strong> {m.cc}</>}
          <br />
          {fmtDateLong(m.date)}
          {m.folder_label && <> · Ordner: {m.folder_label}</>}
        </span>
      )}
      fuss={<span style={{ fontSize: 12, color: "#94a3b8" }}>Esc schließt</span>}
    >
      <div
        data-testid="postfach-live-hinweis"
        style={{
          margin: "12px 18px 0", padding: "8px 12px", fontSize: 12.5, color: "#334155",
          background: "#f1f5f9", borderLeft: "3px solid #94a3b8", borderRadius: 6,
        }}
      >
        Live aus dem Postfach geladen – wird im Portal nicht gespeichert.
      </div>
      {mail.isLoading && (
        <div style={{ padding: 40, textAlign: "center", color: "#94a3b8", fontSize: 13 }}>
          Lade Mail aus dem Postfach …
        </div>
      )}
      {(mail.isError || (m && (!m.verfuegbar || (!m.gefunden && m.fehler)))) && (
        <div style={{ padding: 24, color: "#991b1b", fontSize: 13 }}>
          Mail konnte nicht geladen werden{m?.fehler ? `: ${m.fehler}` : ""}.
        </div>
      )}
      {m && m.verfuegbar && !m.gefunden && !m.fehler && (
        <div style={{ padding: 24, color: "#64748b", fontSize: 13 }}>
          Die Mail liegt nicht mehr in diesem Ordner des Postfachs.
        </div>
      )}
      {m?.gefunden && (
        <>
          <div style={{ padding: "14px 18px" }}>
            {/* Ohne emailId: eingebettete cid-Bilder bleiben leer — der
                Inline-Bild-Endpoint liest aus der Datenbank, und dort ist die
                Mail nicht. Gerendert wird wie überall im sandboxed iframe. */}
            <EmailBody html={m.body_html || m.body_text || ""} minHeight={220} />
          </div>
          {(m.attachments || []).length > 0 && (
            <div style={{ padding: "0 18px 16px" }}>
              <div style={{
                display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 700,
                textTransform: "uppercase", letterSpacing: 0.4, color: "#94a3b8", marginBottom: 8,
              }}>
                <Paperclip style={{ width: 12, height: 12 }} /> Anhänge
              </div>
              {(m.attachments || []).map((a) => (
                <a
                  key={a.index}
                  data-testid="postfach-anhang"
                  href={`/api/v1/emails/postfach-mail/anhang?${postfachParams({ ...kennung, index: String(a.index) })}`}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 6, marginRight: 8, marginBottom: 6,
                    fontSize: 12.5, color: "#0f172a", border: "1px solid #e2e8f0", borderRadius: 8,
                    padding: "4px 10px", textDecoration: "none",
                  }}
                >
                  <Download style={{ width: 12, height: 12 }} />
                  {a.filename} <span style={{ color: "#94a3b8" }}>{groesse(a.size)}</span>
                </a>
              ))}
            </div>
          )}
        </>
      )}
    </ModalShell>,
    document.body,
  )
}
